import { TOUR_IDS, type TourId } from './tourIds'

export interface TourStepDef {
  /** CSS selector; omit for centered popover */
  element?: string
  route?: string
  popover: {
    title: string
    description: string
    side?: 'top' | 'right' | 'bottom' | 'left'
    align?: 'start' | 'center' | 'end'
  }
}

export interface TourDefinition {
  id: TourId
  title: string
  description: string
  steps: TourStepDef[]
}

const sel = (name: string) => `[data-tour="${name}"]`

export const TOUR_DEFINITIONS: TourDefinition[] = [
  {
    id: TOUR_IDS.PLATFORM_OVERVIEW,
    title: 'Platform overview',
    description: 'Navigate the sidebar, workflow trail, and help.',
    steps: [
      {
        route: '/',
        popover: {
          title: 'Welcome to MIM GRC',
          description:
            'This platform automates MIM compliance checks on distributor marketing pages with evidence-backed findings.',
        },
      },
      {
        element: sel('workflow-trail'),
        route: '/',
        popover: {
          title: 'Workflow trail',
          description: 'Follow these seven steps when setting up compliance from scratch.',
          side: 'bottom',
        },
      },
      {
        element: sel('sidebar-nav'),
        route: '/',
        popover: {
          title: 'Sidebar navigation',
          description: 'Access Dashboard, MIM Setup, Products, Distributors, checks, jobs, and findings.',
          side: 'right',
        },
      },
      {
        element: sel('sidebar-mim-setup'),
        route: '/',
        popover: {
          title: 'MIM Setup',
          description: 'Configure master data, families, compared attributes, and compliance rules.',
          side: 'right',
        },
      },
      {
        element: sel('sidebar-run-checks'),
        route: '/',
        popover: {
          title: 'Create Jobs & Check Jobs',
          description: 'Start comparison runs from Create Jobs and monitor them under Check Jobs.',
          side: 'right',
        },
      },
      {
        element: sel('header-help'),
        route: '/',
        popover: {
          title: 'Help & guided tours',
          description: 'Open Help anytime for this guide and topic-specific spotlight tours.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.SETUP_MASTER_DATA,
    title: 'Master data',
    description: 'Create canonical attributes in MIM Setup.',
    steps: [
      {
        route: '/pim?tab=master-data',
        popover: {
          title: 'MIM Setup — Master data',
          description: 'Tab 1 defines reusable canonical attributes for all product families.',
        },
      },
      {
        element: sel('mim-setup-tabs'),
        route: '/pim?tab=master-data',
        popover: { title: 'Setup tabs', description: 'Work left to right: Master data → Families → Compared attributes → Compliance rules.', side: 'bottom' },
      },
      {
        element: sel('mim-master-data-form'),
        route: '/pim?tab=master-data',
        popover: {
          title: 'Create an attribute',
          description: 'Enter term, category, data type, and aliases. Click Create draft attribute when ready.',
          side: 'right',
        },
      },
      {
        element: sel('mim-master-data-library'),
        route: '/pim?tab=master-data',
        popover: {
          title: 'Master data library',
          description: 'Click a row to edit. These attributes are imported into families on the next tab.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.SETUP_FAMILIES,
    title: 'Product families',
    description: 'Create families and attach master-data attributes.',
    steps: [
      {
        route: '/pim?tab=families',
        popover: { title: 'Families tab', description: 'Product families group SKUs that share the same compared fields.' },
      },
      {
        element: sel('mim-family-name'),
        route: '/pim?tab=families',
        popover: { title: 'Family name', description: 'Enter a name and click Add to create a family, or select an existing family from the list.', side: 'right' },
      },
      {
        element: sel('mim-family-list'),
        route: '/pim?tab=families',
        popover: { title: 'Family list', description: 'Select a family to manage its attributes. Product counts show catalogue usage.', side: 'right' },
      },
      {
        element: sel('mim-family-add-attribute'),
        route: '/pim?tab=families',
        popover: {
          title: 'Add attribute',
          description: 'Attach master-data rows to this family. Only linked attributes appear on Compared attributes.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.SETUP_COMPARED_ATTRIBUTES,
    title: 'Compared attributes',
    description: 'Set compare modes per family attribute.',
    steps: [
      {
        route: '/pim?tab=attributes',
        popover: { title: 'Compared attributes', description: 'Configure how each family field is compared during compliance runs.' },
      },
      {
        element: sel('mim-compare-family'),
        route: '/pim?tab=attributes',
        popover: { title: 'Select family', description: 'Choose the product family whose comparison settings you are editing.', side: 'bottom' },
      },
      {
        element: sel('mim-compare-list'),
        route: '/pim?tab=attributes',
        popover: { title: 'Attribute rows', description: 'Select a row to set Exact, Pattern, or Semantic compare mode.', side: 'right' },
      },
      {
        element: sel('mim-compare-settings'),
        route: '/pim?tab=attributes',
        popover: {
          title: 'Compare mode & AI',
          description: 'Exact for codes and numbers; Semantic for marketing copy. Enable AI second opinion only where needed.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.SETUP_COMPLIANCE_RULES,
    title: 'Compliance rules',
    description: 'Create and approve rule packs.',
    steps: [
      {
        route: '/pim?tab=rules',
        popover: { title: 'Compliance rules', description: 'Rule packs contain MIM terminology and structured checks used in Run Checks.' },
      },
      {
        element: sel('mim-rules-packs'),
        route: '/pim?tab=rules',
        popover: { title: 'Rule packs', description: 'Create a draft pack, add rules, then approve. Only approved packs can run checks.', side: 'right' },
      },
      {
        element: sel('mim-rules-editor'),
        route: '/pim?tab=rules',
        popover: {
          title: 'Add rules',
          description: 'Each rule has a phrase, severity, type, and action. Save the pack and set status to Approved.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.PRODUCTS_CATALOG,
    title: 'Products',
    description: 'Import catalogue and PIM comparison values.',
    steps: [
      {
        route: '/products',
        popover: { title: 'Product library', description: 'Kingspan source of truth for codes, families, and comparison values.' },
      },
      {
        element: sel('products-import'),
        route: '/products',
        popover: { title: 'Import products', description: 'Bulk-load CSV with Code, Name, Country, Division, Family, Market, KingspanUrl.', side: 'bottom' },
      },
      {
        element: sel('products-pim-import'),
        route: '/products',
        popover: { title: 'Import product data (PIM)', description: 'Upload long-format PIM export to populate attribute values.', side: 'bottom' },
      },
      {
        element: sel('products-add'),
        route: '/products',
        popover: { title: 'Add product', description: 'Create a single SKU manually when you are not using CSV import.', side: 'bottom' },
      },
      {
        element: sel('products-filters'),
        route: '/products',
        popover: { title: 'Filters', description: 'Search and filter the catalogue, then select a product to edit details and comparison table.', side: 'top' },
      },
    ],
  },
  {
    id: TOUR_IDS.DISTRIBUTORS,
    title: 'Distributors',
    description: 'Register sites and discover product pages.',
    steps: [
      {
        route: '/distributors',
        popover: { title: 'Distributors', description: 'Third-party organisations whose marketing pages you audit.' },
      },
      {
        element: sel('distributors-add'),
        route: '/distributors',
        popover: { title: 'Add distributor', description: 'Enter root URL, company, country, division, and owner.', side: 'bottom' },
      },
      {
        element: sel('distributors-list'),
        route: '/distributors',
        popover: { title: 'Distributor list', description: 'Select a row to open the detail sidebar and manage targets.', side: 'right' },
      },
      {
        element: sel('distributors-detail'),
        route: '/distributors',
        popover: {
          title: 'Detail sidebar',
          description: 'Update contacts and use Discover Product Pages to find product URLs on the site.',
          side: 'left',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.TERM_WATCHES,
    title: 'Term watcher',
    description: 'Monitor keywords on search engines.',
    steps: [
      {
        route: '/term-watches',
        popover: {
          title: 'Term watcher',
          description: 'Separate from product comparison jobs — monitors search-engine results on a schedule.',
        },
      },
      {
        element: sel('term-watches-new'),
        route: '/term-watches',
        popover: { title: 'New watch', description: 'Create a watch with keyword, country, schedule, and notify email.', side: 'left' },
      },
      {
        element: sel('term-watches-list'),
        route: '/term-watches',
        popover: { title: 'Watch list', description: 'Run now, pause, or open scan history for each watch.', side: 'top' },
      },
    ],
  },
  {
    id: TOUR_IDS.SCHEDULE_CHECKS,
    title: 'Schedule checks',
    description: 'Set up recurring comparison jobs.',
    steps: [
      {
        route: '/run-checks',
        popover: { title: 'Run Checks', description: 'Start one-off or scheduled product comparison jobs against distributor targets.' },
      },
      {
        element: sel('run-checks-scope'),
        route: '/run-checks',
        popover: {
          title: 'Check scope',
          description: 'For recurring schedules, select "Select one product and specific distributor URLs". Bulk modes are one-off only.',
          side: 'bottom',
        },
      },
      {
        element: sel('run-checks-rulepack'),
        route: '/run-checks',
        popover: { title: 'Rule pack', description: 'Choose an Approved rule pack — draft packs cannot run.', side: 'bottom' },
      },
      {
        element: sel('run-checks-schedule'),
        route: '/run-checks',
        popover: {
          title: 'Scheduled (recurring)',
          description: 'Set mode to Scheduled, pick a cron preset, and click Save scheduled run.',
          side: 'top',
        },
      },
    ],
  },
  {
    id: TOUR_IDS.MONITOR_JOBS,
    title: 'Monitor jobs',
    description: 'Track check runs and Hangfire queue.',
    steps: [
      {
        route: '/check-runs',
        popover: { title: 'Jobs', description: 'All compliance check runs and background queue status.' },
      },
      {
        element: sel('jobs-hangfire'),
        route: '/check-runs',
        popover: { title: 'Hangfire queue', description: 'Enqueued, processing, scheduled, and failed background jobs.', side: 'bottom' },
      },
      {
        element: sel('jobs-filter'),
        route: '/check-runs',
        popover: { title: 'Status filter', description: 'Filter by In progress, Completed, Failed, or view all.', side: 'bottom' },
      },
      {
        element: sel('jobs-table'),
        route: '/check-runs',
        popover: { title: 'Job list', description: 'Click a row for progress log, PDF export, and linked findings.', side: 'top' },
      },
    ],
  },
  {
    id: TOUR_IDS.FINDINGS_REPORTS,
    title: 'Findings & reports',
    description: 'Triage findings and export distributor packs.',
    steps: [
      {
        route: '/findings',
        popover: { title: 'Findings', description: 'Evidence-backed gaps between Kingspan source of truth and distributor content.' },
      },
      {
        element: sel('findings-filters'),
        route: '/findings',
        popover: { title: 'Filters', description: 'Filter by severity, status, and distributor. Expand rows for evidence.', side: 'bottom' },
      },
      {
        element: sel('findings-export'),
        route: '/findings',
        popover: { title: 'Export CSV', description: 'Export findings for working sessions and distributor follow-up.', side: 'left' },
      },
      {
        element: sel('sidebar-distributor-reports'),
        route: '/findings',
        popover: {
          title: 'Distributor reports',
          description: 'Open Distributor reports in the sidebar to build formal PDF packs with intro/outro text.',
          side: 'right',
        },
      },
      {
        route: '/distributor-action-reports',
        popover: {
          title: 'Distributor action reports',
          description: 'Name the report, set filters, preview, save, and export PDF for distributors.',
        },
      },
    ],
  },
]

export function getTourDefinition(tourId: TourId): TourDefinition {
  const tour = TOUR_DEFINITIONS.find((t) => t.id === tourId)
  if (!tour) throw new Error(`Unknown tour: ${tourId}`)
  return tour
}
